import type { JSX } from "solid-js";
import { mergeProps, Show } from "solid-js";
import type { IconName } from "../icon/Icon";
import { Icon } from "../icon/Icon";
import Text from "../text/Text";
import { isNil } from "lodash-es";

export type PillVariant = "neutral" | "success" | "warning" | "danger" | "informative" | "upgrade";

export type PillProps = {
  variant: PillVariant;
  label: string;
  icon?: IconName;
  inverted?: boolean;
  truncate?: boolean;
  class?: string;
};

const Pill = (rawProps: PillProps): JSX.Element => {
  const props = mergeProps({ inverted: false, truncate: true }, rawProps);

  return (
    <span
      class="inline-flex h-4 items-center gap-0.5 rounded-sm px-1 text-small-medium"
      classList={{
        "max-w-36.5": props.truncate,
        [props.class ?? ""]: !isNil(props.class),
        ...(props.inverted
          ? {
              "text-inverse": true,
              "bg-surface-status-neutral": props.variant === "neutral",
              "bg-surface-status-success": props.variant === "success",
              "bg-surface-status-warning": props.variant === "warning",
              "bg-surface-status-danger": props.variant === "danger",
              "bg-surface-status-informative": props.variant === "informative",
              "bg-surface-status-upgrade": props.variant === "upgrade",
            }
          : {
              "bg-surface-status-subtle-neutral text-status-neutral": props.variant === "neutral",
              "bg-surface-status-subtle-success text-status-success": props.variant === "success",
              "bg-surface-status-subtle-warning text-status-warning": props.variant === "warning",
              "bg-surface-status-subtle-danger text-status-danger": props.variant === "danger",
              "bg-surface-status-subtle-informative text-status-informative": props.variant === "informative",
              "bg-surface-status-subtle-upgrade text-status-upgrade": props.variant === "upgrade",
            }),
      }}
    >
      <Show when={props.icon}>
        {(icon) => (
          <Icon
            name={icon()}
            type="solid"
            size="xs"
            class="inline"
            classList={
              props.inverted
                ? { "icon-inverse": true }
                : {
                    "icon-status-neutral": props.variant === "neutral",
                    "icon-status-success": props.variant === "success",
                    "icon-status-warning": props.variant === "warning",
                    "icon-status-danger": props.variant === "danger",
                    "icon-status-informative": props.variant === "informative",
                    "icon-status-upgrade": props.variant === "upgrade",
                  }
            }
          />
        )}
      </Show>
      <Text class="min-w-2 text-center" text={props.label} truncate={props.truncate} />
    </span>
  );
};

export default Pill;
